import { useCallback, useEffect, useRef, useState } from 'react'

import { useFetch } from '@reveni-io/reveni-ui'

import { aiFlowStates } from '@/common/components/SendEmailDrawer/constants'
import parcelServices from '@/parcels/services/services'

import { transformRequestGenerateAiEmail } from './transformers'

const useAiEmailGeneration = ({
  storeId,
  relatedId,
  setToastInfo,
  setSubject,
  setBody,
  destination,
  subject,
  body,
  showSendEmailModal,
}) => {
  const [generatedSubject, setGeneratedSubject] = useState('')
  const [generatedBody, setGeneratedBody] = useState('')
  const [generating, setGenerating] = useState(false)
  const [aiFlowState, setAiFlowState] = useState(aiFlowStates.initial)
  const [userInquiry, setUserInquiry] = useState('')
  const [preAiSubject, setPreAiSubject] = useState('')
  const [preAiBody, setPreAiBody] = useState('')
  const previousStateRef = useRef(aiFlowStates.initial)
  const cancelledRef = useRef(false)

  const { fetchDataCancellable: fetchAiEmail, cancel: cancelFetch } = useFetch(parcelServices.generateAiEmail, {
    doInitialRequest: false,
  })

  const generateEmail = useCallback(
    async ({ destination, draftSubject, draftBody, userInquiry }) => {
      if (aiFlowState === aiFlowStates.initial) {
        setPreAiSubject(draftSubject || '')
        setPreAiBody(draftBody || '')
      }

      previousStateRef.current = aiFlowState
      cancelledRef.current = false
      setAiFlowState(aiFlowStates.generating)
      setGenerating(true)
      setSubject('')
      setBody('')

      try {
        const data = await fetchAiEmail({
          storeId,
          relatedId,
          payload: transformRequestGenerateAiEmail({ destination, draftSubject, draftBody, userInquiry }),
        })

        setGeneratedSubject(data.subject)
        setGeneratedBody(data.body)
        setSubject(data.subject)
        setBody(data.body)
        setGenerating(false)
        setAiFlowState(aiFlowStates.pendingFeedback)
        setUserInquiry('')
      } catch (error) {
        if (cancelledRef.current) return

        setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
        setGenerating(false)
        setAiFlowState(previousStateRef.current)
      }
    },
    [storeId, relatedId, setToastInfo, setSubject, setBody, aiFlowState, fetchAiEmail]
  )

  const acceptAiContent = useCallback(() => {
    setAiFlowState(aiFlowStates.initial)
  }, [])

  const requestImprovement = useCallback(() => {
    setAiFlowState(aiFlowStates.improving)
  }, [])

  const discardAiContent = useCallback(() => {
    setSubject(preAiSubject)
    setBody(preAiBody)
    setGeneratedSubject('')
    setGeneratedBody('')
    setUserInquiry('')
    setAiFlowState(aiFlowStates.initial)
  }, [preAiSubject, preAiBody, setSubject, setBody])

  const cancelGeneration = useCallback(() => {
    cancelledRef.current = true
    cancelFetch()
    setGenerating(false)
    setAiFlowState(previousStateRef.current)
  }, [cancelFetch])

  const reset = useCallback(() => {
    setGeneratedSubject('')
    setGeneratedBody('')
    setAiFlowState(aiFlowStates.initial)
    setUserInquiry('')
    setPreAiSubject('')
    setPreAiBody('')
  }, [])

  const onGenerateAiEmail = useCallback(() => {
    generateEmail({ destination, draftSubject: subject, draftBody: body, userInquiry })
  }, [generateEmail, destination, subject, body, userInquiry])

  useEffect(() => {
    if (!showSendEmailModal) {
      reset()
    }
  }, [showSendEmailModal, reset])

  return {
    generateEmail,
    generatedSubject,
    generatedBody,
    generating,
    reset,
    aiFlowState,
    userInquiry,
    setUserInquiry,
    acceptAiContent,
    requestImprovement,
    discardAiContent,
    cancelGeneration,
    onGenerateAiEmail,
  }
}

export default useAiEmailGeneration
